import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import {
  Navigate,
  Link as RouterLink,
  useLocation,
  useNavigate,
} from "react-router-dom";

import ClientAuthLayout from "../components/ClientAuthLayout";
import { useDataStore } from "../dataStore";
import { useClientPortal } from "../clientPortal/ClientPortalContext";
import {
  CLIENT_PORTAL_PATHS,
} from "../clientPortal/clientPortalConstants";
import { safeClientPortalReturnPath } from "../clientPortal/RequireClientPortal";
import { DEFAULT_BRAND_THEME } from "../themeTokens";

export default function ClientLoginPage() {
  const { brandTheme, t } = useDataStore();
  const c = { ...DEFAULT_BRAND_THEME, ...brandTheme };
  const {
    configReady,
    enabled,
    status,
    isAuthenticated,
    authError,
    login,
  } = useClientPortal();
  const location = useLocation();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitError, setSubmitError] = useState("");

  const restoring = status === "restoring";
  const busy = status === "authenticating" || restoring;
  const returnPath = safeClientPortalReturnPath(location.state?.from);
  const expired = Boolean(location.state?.sessionExpired);

  useEffect(() => {
    setSubmitError("");
  }, [username, password]);

  if (configReady === false) {
    return (
      <Box
        role="status"
        aria-label={t("portal.guard.restoring")}
        sx={{ minHeight: 240, display: "grid", placeItems: "center" }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!enabled) return <Navigate to="/" replace />;
  if (isAuthenticated) {
    return <Navigate to={returnPath || CLIENT_PORTAL_PATHS.account} replace />;
  }

  const onSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;

    const result = await login({ username, password });
    if (result.ok) {
      navigate(returnPath || CLIENT_PORTAL_PATHS.account, { replace: true });
      return;
    }
    setSubmitError(true);
  };

  const errorMessage =
    submitError || authError ? t("portal.login.failed") : "";

  const aside = (
    <Stack spacing={2.25}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "16px",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.18)",
        }}
      >
        <PersonAddAlt1RoundedIcon
          sx={{ fontSize: 28, color: "#fff" }}
          aria-hidden="true"
        />
      </Box>
      <Box>
        <Typography component="h2" variant="h5" fontWeight={950}>
          {t("portal.login.createTitle")}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
          {t("portal.login.createText")}
        </Typography>
      </Box>
      <Button
        component={RouterLink}
        to={CLIENT_PORTAL_PATHS.register}
        variant="outlined"
        sx={{
          alignSelf: "flex-start",
          minHeight: 46,
          px: 2.25,
          borderRadius: "14px",
        }}
      >
        {t("portal.login.createAction")}
      </Button>
    </Stack>
  );

  return (
    <ClientAuthLayout aside={aside}>
      <Stack spacing={{ xs: 2.25, sm: 2.75 }}>
        <Stack
          spacing={1.1}
          alignItems={{ xs: "center", sm: "flex-start" }}
          textAlign={{ xs: "center", sm: "left" }}
        >
          <Chip
            icon={<ShieldOutlinedIcon />}
            label={t("portal.account.title")}
            size="small"
            sx={{
              color: c.text,
              bgcolor: "rgba(247,181,0,0.14)",
              border: "1px solid rgba(124,45,18,0.10)",
              "& .MuiChip-icon": { color: c.accent },
            }}
          />
          <Box
            sx={{
              width: 62,
              height: 62,
              borderRadius: "19px",
              display: "grid",
              placeItems: "center",
              bgcolor: c.primary,
              color: c.onPrimary,
              boxShadow: "0 16px 34px rgba(247,181,0,0.22)",
            }}
          >
            <LockRoundedIcon />
          </Box>
          <Typography
            component="h1"
            variant="h4"
            sx={{
              color: c.text,
              fontSize: { xs: 30, sm: 38 },
              lineHeight: 1.08,
            }}
          >
            {t("portal.login.title")}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ maxWidth: 520, lineHeight: 1.6 }}
          >
            {t("portal.login.subtitle")}
          </Typography>
        </Stack>

        {expired ? (
          <Alert severity="info">
            {t("portal.login.expired")}
          </Alert>
        ) : null}

        {errorMessage ? (
          <Alert id="client-login-error" severity="error" role="alert">
            {errorMessage}
          </Alert>
        ) : null}

        <Box
          component="form"
          onSubmit={onSubmit}
          aria-busy={busy}
          noValidate
        >
          <Stack spacing={2}>
            <TextField
              id="client-username"
              label={t("portal.login.username")}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              required
              fullWidth
              disabled={busy}
              aria-describedby={errorMessage ? "client-login-error" : undefined}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineRoundedIcon
                      fontSize="small"
                      sx={{ color: c.muted }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="client-password"
              label={t("portal.login.password")}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              fullWidth
              disabled={busy}
              aria-describedby={errorMessage ? "client-login-error" : undefined}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockRoundedIcon
                      fontSize="small"
                      sx={{ color: c.muted }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              component={RouterLink}
              to={CLIENT_PORTAL_PATHS.forgotPassword}
              variant="text"
              sx={{
                alignSelf: "flex-end",
                minHeight: 40,
                px: 1,
                color: c.text,
              }}
            >
              {t("portal.login.forgotPassword")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !username.trim() || !password}
              sx={{
                minHeight: 52,
                mt: 0.5,
                borderRadius: "15px",
                bgcolor: c.primary,
                color: c.onPrimary,
                boxShadow: "0 14px 30px rgba(247,181,0,0.22)",
                "&:hover": {
                  bgcolor: c.primary,
                  transform: "translateY(-1px)",
                },
              }}
            >
              {busy ? (
                <>
                  <CircularProgress
                    size={18}
                    color="inherit"
                    sx={{ mr: 1 }}
                  />
                  {restoring
                    ? t("portal.login.restoring")
                    : t("portal.login.pending")}
                </>
              ) : (
                t("portal.login.submit")
              )}
            </Button>
          </Stack>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: "14px",
            bgcolor: "rgba(17,17,17,0.035)",
            border: "1px solid rgba(17,17,17,0.06)",
          }}
        >
          {t("portal.login.optional")}
        </Typography>
      </Stack>
    </ClientAuthLayout>
  );
}
